import React, { useEffect, useMemo, useState } from 'react';
import { InputLabel, MenuItem, Select } from '@material-ui/core';
import { StyledKakarikoSelect, StyledKakarikoSelectRoot } from './styles';

import type { IKakarikoSelectProps } from './types';

export const KakarikoSelect = (props: IKakarikoSelectProps) => {
  const {
    ariaLabel,
    autoSelect = true,
    className = '',
    title,
    displayValue,
    errorMessage,
    handleBlur,
    handleChange,
    hasError,
    isEditable,
    isEditing = true,
    label = '',
    options,
    optional,
    value,
    variant,
    width = 'full',
  } = props;

  const [internalValue, setInternalValue] = useState(value);
  const [touched, setTouched] = useState(false);

  const optionArray = useMemo((): string[] => {
    if (displayValue) {
      return options.map((option) => {
        return displayValue(option);
      });
    }
    return options.map((option) => option?.label || option);
  }, [displayValue, options]);

  const memoValue = useMemo((): any => {
    const optionItem = options?.find((option) => {
      if (typeof option === 'string' || typeof option === 'number')
        return option === internalValue;
      if (displayValue)
        return displayValue(option) === displayValue(internalValue);
      return (
        option?.value === internalValue?.value ||
        option?.label === internalValue?.label
      );
    });
    return optionItem ?? '';
  }, [displayValue, options, internalValue]);

  const optionRender = useMemo(() => {
    return (
      // Option requires an `any` designation to not throw off typing of MenuItem from Material UI.
      options.map((option: any, index: number) => {
        return (
          <MenuItem key={optionArray[index]} value={option}>
            {optionArray[index]}
          </MenuItem>
        );
      })
    );
  }, [optionArray, options]);

  const handleChangeInternal = (value: any) => {
    setInternalValue(value);
    if (handleChange) handleChange(value);
  };

  useEffect(() => {
    if (autoSelect && options.length === 1) handleChangeInternal(options[0]);
    if (internalValue && Object.keys(internalValue).length) setTouched(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBlurInternal = () => {
    setTouched(true);
    if (handleBlur) handleBlur(value);
  };

  return (
    <StyledKakarikoSelectRoot
      className={className}
      title={title}
      $width={width}
    >
      {isEditing && isEditable !== false ? (
        <StyledKakarikoSelect
          error={touched && hasError}
          required={hasError !== undefined && optional !== true}
          variant={variant}
          $width={width}
        >
          {label && <InputLabel id={label}>{label}</InputLabel>}
          <Select
            error={touched && hasError}
            inputProps={{
              'aria-required': hasError !== undefined && optional !== true,
              ...(ariaLabel && { 'aria-label': ariaLabel }),
            }}
            onBlur={() => handleBlurInternal()}
            onChange={(option: any) =>
              handleChangeInternal(option.target.value)
            }
            value={memoValue}
          >
            {optionRender}
          </Select>
        </StyledKakarikoSelect>
      ) : (
        <span>
          {(displayValue && displayValue(memoValue)) ||
            memoValue?.label ||
            memoValue}
        </span>
      )}
      {hasError && touched && errorMessage && (
        <div className="error">{errorMessage}</div>
      )}
    </StyledKakarikoSelectRoot>
  );
};

export default KakarikoSelect;
